import { useState } from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { Button } from "@/components/ui/button"

type PersonFormProps = {
  id: number | null
  name: string
  content: string | null
  birth_date: string | null
  birth_date_precision: string | null
  birth_place: string | null
  death_date: string | null
  death_date_precision: string | null
  death_place: string | null
  death_age: number | null
  meta_title: string | null
  meta_description: string | null
  meta_keywords: string | null
  all_recordings_added: boolean
  in_amh: boolean
  amh_url: string | null
}

type PageProps = { person: PersonFormProps; errors?: Record<string, string> }

const DATE_PRECISIONS = ["day", "month", "day_month", "year", "unknown"]

export default function AdminPersonForm({ person }: { person: PersonFormProps }) {
  const { errors = {} } = usePage<PageProps>().props
  const isNew = person.id === null
  const [form, setForm] = useState({
    name: person.name ?? "",
    content: person.content ?? "",
    birth_date: person.birth_date ?? "",
    birth_date_precision: person.birth_date_precision ?? "",
    birth_place: person.birth_place ?? "",
    death_date: person.death_date ?? "",
    death_date_precision: person.death_date_precision ?? "",
    death_place: person.death_place ?? "",
    death_age: person.death_age !== null ? String(person.death_age) : "",
    meta_title: person.meta_title ?? "",
    meta_description: person.meta_description ?? "",
    meta_keywords: person.meta_keywords ?? "",
    all_recordings_added: person.all_recordings_added ?? false,
    in_amh: person.in_amh ?? false,
    amh_url: person.amh_url ?? "",
  })

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
    const target = e.target as HTMLInputElement
    setForm((prev) => ({
      ...prev,
      [target.name]: target.type === "checkbox" ? target.checked : target.value,
    }))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const path = isNew ? "/admin/people" : `/admin/people/${person.id}`
    if (isNew) {
      router.post(path, { person: form })
    } else {
      router.patch(path, { person: form })
    }
  }

  const title = isNew ? "New person" : `Edit ${person.name}`

  return (
    <>
      <Head title={title}>
        <meta name="description" content={`${title} in the admin area.`} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={`${title} in the admin area.`} />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <h1>{title}</h1>
        </div>

        <form onSubmit={handleSubmit} className="mt-6 max-w-xl space-y-4">
          <Field label="Name" error={errors.name}>
            <input name="name" type="text" value={form.name} onChange={handleChange} className={inputClass} />
          </Field>

          <Field label="Content">
            <textarea name="content" rows={5} value={form.content} onChange={handleChange} className={inputClass} />
          </Field>

          <Field label="Birth date">
            <input name="birth_date" type="date" value={form.birth_date} onChange={handleChange} className={inputClass} />
          </Field>

          <Field label="Birth date precision">
            <select name="birth_date_precision" value={form.birth_date_precision} onChange={handleChange} className={inputClass}>
              <option value="">— none —</option>
              {DATE_PRECISIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>

          <Field label="Birth place">
            <input name="birth_place" type="text" value={form.birth_place} onChange={handleChange} className={inputClass} />
          </Field>

          <Field label="Death date">
            <input name="death_date" type="date" value={form.death_date} onChange={handleChange} className={inputClass} />
          </Field>

          <Field label="Death date precision">
            <select name="death_date_precision" value={form.death_date_precision} onChange={handleChange} className={inputClass}>
              <option value="">— none —</option>
              {DATE_PRECISIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>

          <Field label="Death place">
            <input name="death_place" type="text" value={form.death_place} onChange={handleChange} className={inputClass} />
          </Field>

          <Field label="Death age">
            <input name="death_age" type="number" value={form.death_age} onChange={handleChange} className={inputClass} />
          </Field>

          <Field label="Meta title">
            <input name="meta_title" type="text" value={form.meta_title} onChange={handleChange} className={inputClass} />
          </Field>

          <Field label="Meta description">
            <input name="meta_description" type="text" value={form.meta_description} onChange={handleChange} className={inputClass} />
          </Field>

          <Field label="Meta keywords">
            <input name="meta_keywords" type="text" value={form.meta_keywords} onChange={handleChange} className={inputClass} />
          </Field>

          <div className="flex items-center gap-2">
            <input id="all_recordings_added" name="all_recordings_added" type="checkbox"
              checked={form.all_recordings_added} onChange={handleChange} className="h-4 w-4" />
            <label htmlFor="all_recordings_added" className="text-sm text-ink-body">All recordings added</label>
          </div>

          <div className="flex items-center gap-2">
            <input id="in_amh" name="in_amh" type="checkbox"
              checked={form.in_amh} onChange={handleChange} className="h-4 w-4" />
            <label htmlFor="in_amh" className="text-sm text-ink-body">In AMH</label>
          </div>

          <div>
            <label htmlFor="amh_url" className="block text-sm text-ink-muted mb-1">AMH URL</label>
            <input id="amh_url" name="amh_url" type="url" value={form.amh_url} onChange={handleChange}
              placeholder="https://australianmusichistory.com/…" className={inputClass} />
            <p className="mt-1 text-xs text-ink-muted">The record's post on australianmusichistory.com, if it has one. Setting it ticks In AMH.</p>
            {errors.amh_url && <p className="mt-1 text-xs text-red-600">{errors.amh_url}</p>}
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Button type="submit">{isNew ? "Create person" : "Update person"}</Button>
            <Link href="/admin/people" className="text-sm text-ink-muted no-underline hover:text-ink-display">Cancel</Link>
          </div>
        </form>
      </AdminShell>
    </>
  )
}

const inputClass = "w-full rounded border border-hairline bg-page px-3 py-2 text-sm text-ink-body focus:outline-none focus:ring-1 focus:ring-current"

function Field({ label, children, error }: { label: string; children: React.ReactNode; error?: string }) {
  return (
    <div>
      <label className="block text-sm text-ink-muted mb-1">{label}</label>
      {children}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  )
}
